import { Injectable, Optional } from '@angular/core';

import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, ReplaySubject } from 'rxjs';
import { SsoConfigurationService } from './sso-configuration.service';
import { MsalService } from '@azure/msal-angular';
import { AccountInfo, IPublicClientApplication, RedirectRequest } from '@azure/msal-browser';

@Injectable({
	providedIn: 'root'
})
export class SsoService {

	ATTRIBUT_DECONNEXION_MANUELLE = 'deconnexion_manuelle';

	url: string = '/ws/sso';

	// Le token d'accès utile seulement pour les applications en cohabitation
	accessTokenLocal: string;
	// Le nom est utile seulement pour les applications en cohabitation
	nomUtilisateurMicrosoftConnecte: string;
	// Instance du client d'interrogation SSO de microsoft pour les applications en cohabitation
	instancePourCohabitation: IPublicClientApplication;

	constructor(private httpClient: HttpClient, private ssoConfigurationService: SsoConfigurationService, @Optional() private msalService: MsalService) {
	}

	/**
	 * Donne l'instance du client d'interrogation SSO de microsoft à utiliser
	 */
	getIPublicClientApplication(): IPublicClientApplication {
		return this.instancePourCohabitation ?? this.msalService?.instance;
	}

	/**
	 * Permet de se connecter à l'application grâce au compte microsoft
	 */
	loginAvecMicrosoft(): Observable<void> {
		const finTraitement = new ReplaySubject<void>();

		const accessTokenRequest = {
			scopes: [this.ssoConfigurationService.configurationSsoCourante.protectedResource],
			account: this.getIPublicClientApplication().getAllAccounts()[0]
		};
		this.getIPublicClientApplication().acquireTokenSilent(accessTokenRequest)
			.then(() => {
					let headers = new HttpHeaders();
					if (this.accessTokenLocal) {
						let prefixBearer = '';
						if (this.accessTokenLocal.toLocaleLowerCase().indexOf('bearer') < 0) {
							prefixBearer = 'Bearer ';
						}
						headers = headers.set('Authorization-Azure', prefixBearer + this.accessTokenLocal);
					}

					// supprime le jwt token afin que la requête de connexion ne contienne pas d'ancien token qui pourrait être refusé par le backend
					localStorage.removeItem('access_token');
					this.httpClient.post(`${this.url}/connexion`, {}, { headers: headers, observe: 'response', responseType: 'text' })
						.subscribe({
							next: reponse => {
								// Lors de la connexion avec son compte microsoft on souhaite supprimer du local storage l'attribut de déconnexion manuelle pour que l'utilisateur se connecte automatiquement
								// à l'application sans action de sa part
								localStorage.removeItem(this.ATTRIBUT_DECONNEXION_MANUELLE);
								// A la réception de la réponse, on reçoit un token JWT conforme à l'application Sigale, on le stock dans le local storage pour qu'il soit utilisée dans toutes les requêtes vers le back
								localStorage.setItem('access_token', reponse.headers.get('authorization'));

								finTraitement.next();
							},
							error: error => {
								finTraitement.error(error);
							}
						});
				}
			).catch(() => {
			this.validationSSO();
		});

		return finTraitement;
	}

	/**
	 * Permet de demander la validation de connexion SSO à microsoft
	 */
	validationSSO(): void {
		const request: RedirectRequest = {
			scopes: ['user.read']
		};
		this.getIPublicClientApplication().loginRedirect(request);
	}

	/**
	 * Actions à réaliser lors de la déconnexion de l'utilisateur
	 *
	 * @param manuelle indique si la déconnexion est manuelle
	 */
	deconnexion(manuelle: boolean): void {
		if (manuelle) {
			localStorage.setItem(this.ATTRIBUT_DECONNEXION_MANUELLE, String(manuelle));
		}
	}

	/**
	 * Stocke dans le service l'access token d'azure
	 *
	 * @param accessTokenLocal l'access token azure
	 * @param accountInfo les informations du compte miscrosoft connecté
	 */
	enregistrerAccessTokenAzure(accessTokenLocal: string, accountInfo: AccountInfo): void {
		this.accessTokenLocal = accessTokenLocal;
		this.nomUtilisateurMicrosoftConnecte = accountInfo?.name;
	}

	/**
	 * Détermine si le Sso est actif
	 * @return true si le sso est actif
	 */
	ssoActif(): boolean {
		return this.ssoConfigurationService?.ssoActif();
	}

	/**
	 * Récupère le nom de l'utilisateur microsoft connecté
	 */
	recupererNomUtilisateurConnecte(): string {
		if (this.getIPublicClientApplication()) {
			const account = this.getIPublicClientApplication().getActiveAccount()
				|| this.getIPublicClientApplication().getAllAccounts()[0];

			this.nomUtilisateurMicrosoftConnecte = account?.name;
		}
		return this.nomUtilisateurMicrosoftConnecte;
	}
}
